import React, { useState } from 'react';
import { Modal, Button } from '../shared/Modal';
import { Field, TextInput, Select, CheckboxGroup, Divider, SectionTitle } from '../shared/FormFields';
import { Monogram } from './teamUi';
import { DEFAULT_WORKSPACE_SENTINEL, defaultWorkspaceLabel } from '../../utils/defaultWorkspace';

// Invite a new member or change an existing one's role and access — one body,
// two modes, because the controls are identical and only the identity row
// differs.
//
// Role lives here rather than as an icon in the table row: promoting someone is
// a decision about that person, made in the same place as everything else about
// them, and it reads far better next to the access they will have.
//
// Ownership moves through this dialog too, from the row of the person who is
// GAINING it — choose "Owner" and the primary button hands off to the transfer
// confirmation instead of saving. The current owner's own row has no dialog:
// there is nothing about them to edit, and "transfer" asked from their row would
// need a second person-picker to mean anything.
//
// The default workspace (the account level) is the FIRST row of the workspace
// list — one grant among N, which is exactly what the server implements: a
// member's accountLevel grant opens only the account scope's own docs
// (canAccessDocWorkspace), never other workspaces. On the wire it stays the
// `accountLevel` boolean; the 'account' checkbox value is mapped in submit and
// never lands inside workspaceIds.
//
// Mount with a key (member email or 'invite') so the form state resets per target.

const note = { margin: '6px 0 0', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-muted, #94a3b8)' };

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const ROLE_HINTS = {
  member: "Uses the app in the workspaces granted below. Can't manage the team or workspaces.",
  admin: 'Invites and removes people, creates and manages every workspace, and uses every feature. Cannot transfer ownership.',
  owner: 'Makes this person the account owner. You become an Admin. You will be asked to confirm.',
};

export default function MemberAccessModal({
  mode = 'invite',
  email: initialEmail = '',
  accountName,
  workspaces = [],
  initialWorkspaceIds = [],
  initialAccountLevel = false,
  initialRole = 'member',
  canAssignRole = false,
  canAssignOwner = false,
  busy = false,
  onSubmit,
  onClose,
}) {
  const inviting = mode === 'invite';
  const [email, setEmail] = useState(initialEmail);
  // One list: the default workspace (sentinel 'account') alongside the real
  // workspace ids — submit splits them back into the wire's two fields.
  const [selection, setSelection] = useState([
    ...(initialAccountLevel ? [DEFAULT_WORKSPACE_SENTINEL] : []),
    ...initialWorkspaceIds,
  ]);
  const [accountRole, setAccountRole] = useState(initialRole);

  // Picking "Owner" is not a save — it hands off to the type-to-confirm dialog.
  const transferring = accountRole === 'owner';
  const roleOptions = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
    // Ownership can only be moved, never granted alongside something else, so
    // this appears only for someone allowed to move it — and an invite can't
    // carry it at all, since the person doesn't exist on the account yet.
    ...(canAssignOwner && !inviting ? [{ value: 'owner', label: 'Owner — transfers ownership' }] : []),
  ];

  // An Admin needs no explicit workspace grants — they have the whole account —
  // so the "pick something" rule only applies to Members.
  const canSave = !busy
    && (!inviting || email.trim().length > 0)
    && (accountRole !== 'member' || selection.length > 0);

  const submit = () => {
    if (transferring) {
      onSubmit({ intent: 'transfer', email: (inviting ? email : initialEmail).trim() });
      return;
    }
    if (!canSave) return;
    // The default-workspace row maps back to the wire's accountLevel boolean;
    // the filter also guarantees the sentinel never reaches workspaceIds
    // (the server rejects non-hex ids). Role omitted entirely when this caller
    // can't set roles — the server rejects the whole request if it carries a
    // role they aren't allowed to change.
    onSubmit({
      email: email.trim(),
      workspaceIds: selection.filter((v) => v !== DEFAULT_WORKSPACE_SENTINEL),
      accountLevel: selection.includes(DEFAULT_WORKSPACE_SENTINEL),
      ...(canAssignRole ? { accountRole } : {}),
    });
  };

  const primaryLabel = busy
    ? 'Saving…'
    : transferring
      ? 'Transfer ownership…'
      : inviting ? 'Send invite' : 'Save access';

  return (
    <Modal
      title={inviting ? 'Invite a team member' : 'Edit member'}
      width={520}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={transferring ? busy : !canSave}>
            {primaryLabel}
          </Button>
        </>
      )}
    >
      {inviting ? (
        <Field label="Email address">
          <TextInput type="email" value={email} onChange={setEmail} placeholder="teammate@company.com" />
        </Field>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <Monogram label={initialEmail} size={32} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary, #0f172a)', wordBreak: 'break-all' }}>
            {initialEmail}
          </span>
        </div>
      )}

      {/* A workspace owner sees the role but can't change it — they manage
          access to their workspace, not the person's standing in the account. */}
      <Field label="Role" hint={ROLE_HINTS[accountRole]}>
        <Select
          value={accountRole}
          onChange={setAccountRole}
          disabled={!canAssignRole}
          options={canAssignRole ? roleOptions : [{ value: accountRole, label: ROLE_LABELS[accountRole] }]}
        />
      </Field>

      <Divider />
      <SectionTitle>Access</SectionTitle>

      {accountRole === 'admin' ? (
        <p style={{ ...note, marginTop: 0 }}>
          Admins can open every workspace in this account, including ones created later.
        </p>
      ) : transferring ? (
        <p style={{ ...note, marginTop: 0 }}>
          The account owner always has full access, so there is nothing to choose here.
        </p>
      ) : (
        <Field label="Workspaces">
          <CheckboxGroup
            value={selection}
            onChange={setSelection}
            options={[
              { value: DEFAULT_WORKSPACE_SENTINEL, label: defaultWorkspaceLabel(accountName) },
              ...workspaces.map((w) => ({ value: w.id, label: w.name || w.id })),
            ]}
          />
          <p style={note}>The default workspace holds everything not placed in another workspace.</p>
        </Field>
      )}

      {inviting && (
        <p style={{ margin: '4px 0 0', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary, #64748b)' }}>
          People who already have an account get access straight away. Everyone else receives an email, and
          the access applies when they sign up with this address.
        </p>
      )}
    </Modal>
  );
}
